import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useOrderDetailsInfinite } from '../../hooks/useOrderDetailsInfinite';
import { OrderDetailsAccordion } from '../display/OrderDetailsAccordion';

interface OrderDetailsModalData {
  orderIds: number[];
  productName: string;
  totalQuantity: number;
}

// 🎯 Extender la interfaz del ref para incluir método personalizado
interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithOrderIds: (data: OrderDetailsModalData) => void;
}

const OrderDetailsModal = forwardRef<ExtendedModalCenterRef, {}>((_, ref) => {
  // 🎯 Referencia interna al ModalCenter
  const internalModalRef = useRef<ModalCenterRef>(null);

  // 🎯 Estado interno del modal
  const [modalData, setModalData] = useState<OrderDetailsModalData | null>(null);

  // 🎯 Estado para acordeones expandidos
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set());

  // 🎯 Hook para obtener detalles de pedidos con scroll infinito
  const {
    orders: orderDetails,
    isLoading: isLoadingDetails,
    error: detailsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    totalOrders,
    total,
  } = useOrderDetailsInfinite(modalData?.orderIds || [], !!modalData);

  // 🎯 Hook de scroll infinito
  const { lastElementRef } = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    enabled: !!modalData,
  });

  // 🎯 Método para abrir con datos específicos
  const openWithOrderIds = (data: OrderDetailsModalData) => {
    setModalData(data);
    setExpandedOrders(new Set()); // Reset acordeones al abrir
    internalModalRef.current?.openModal();
  };

  // 🎯 Toggle acordeón de pedido
  const toggleOrderExpansion = (orderId: number) => {
    setExpandedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  // 🎯 Exponer métodos usando useImperativeHandle
  useImperativeHandle(
    ref,
    () => ({
      openModal: () => internalModalRef.current?.openModal(),
      closeModal: () => internalModalRef.current?.closeModal(),
      openWithOrderIds,
    }),
    [],
  );

  // Si no hay datos, no renderizar contenido
  if (!modalData) {
    return (
      <ModalCenter ref={internalModalRef} title="Pedidos con este producto" width="xl" type="info">
        <div>Cargando...</div>
      </ModalCenter>
    );
  }

  const { orderIds, productName, totalQuantity } = modalData;

  return (
    <ModalCenter ref={internalModalRef} title="Pedidos con este producto" width="xl" type="info">
      <div className="space-y-4">
        {/* Información del producto */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {totalOrders > 0 ? `${totalOrders} de ${total} pedidos cargados` : `${orderIds.length} pedidos`}
          </span>
          {isFetchingNextPage && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              Cargando más...
            </div>
          )}
        </div>

        {/* Detalles de Pedidos */}
        <div>
          <OrderDetailsAccordion
            orders={orderDetails}
            isLoading={isLoadingDetails}
            error={!!detailsError}
            isFetchingNextPage={isFetchingNextPage}
            lastElementRef={lastElementRef}
            expandedOrders={expandedOrders}
            onToggleExpansion={toggleOrderExpansion}
            enableSelection={false}
            maxHeight="max-h-96"
            showBorder={false}
            loadingMessage="Cargando detalles..."
          />
        </div>
      </div>
    </ModalCenter>
  );
});

OrderDetailsModal.displayName = 'OrderDetailsModal';

export { OrderDetailsModal };
export type { ExtendedModalCenterRef as OrderDetailsModalRef };
